"use client";
import { createContext, useContext, useMemo, useState } from "react";
import { cn } from "~/lib/utils";
import { ChevronUp } from "lucide-react";

/**
 * @typedef {{
 * 	currentExpanded: 'front' | 'details';
 * 	setCurrentExpanded: (expanded: 'front' | 'details' | ((currentExpanded: 'front' | 'details') => 'front' | 'details')) => void;
 * }} ShowcaseCardContextValue
 */

const ShowcaseCardContext = createContext(
  /** @type {ShowcaseCardContextValue} */ ({
    currentExpanded: "front",
    setCurrentExpanded: () => {
      throw new Error("ShowcaseCardContextProvider is not found");
    },
  }),
);

/**
 * @param {import("react").PropsWithChildren<{ defaultExpanded?: 'front' | 'details' }>} props
 */
function ShowcaseCardProvider(props) {
  const [currentExpanded, setCurrentExpanded] = useState(
    props.defaultExpanded ?? "front",
  );

  /** @type {ShowcaseCardContextValue} */
  const value = useMemo(
    () => ({ currentExpanded, setCurrentExpanded }),
    [currentExpanded, setCurrentExpanded],
  );

  return (
    <ShowcaseCardContext.Provider value={value}>
      {props.children}
    </ShowcaseCardContext.Provider>
  );
}

const useShowcaseCard = () => {
  const store = useContext(ShowcaseCardContext);

  if (!store) {
    throw new Error("useShowcaseCard must be used within a ShowcaseCard");
  }

  return store;
};

/** @param {import("react").HTMLAttributes<HTMLDivElement>} props  */
function ShowcaseCard(props) {
  const isDetailsExpanded = useShowcaseCard().currentExpanded === "details";

  return (
    <div
      {...props}
      className={cn(
        "showcase-card relative grid overflow-hidden transition-all duration-700",
        isDetailsExpanded ? "grid-rows-[86%_14%]" : "grid-rows-[100%_0%]",
        props.className,
      )}
    >
      {props.children}
    </div>
  );
}

/** @param {import("react").HTMLAttributes<HTMLDivElement>} props  */
function ShowcaseCardFront(props) {
  const isExpanded = useShowcaseCard().currentExpanded === "front";

  return (
    <div
      {...props}
      className={cn(
        "showcase-card-front",
        props.className,
        isExpanded && "expanded group",
      )}
      data-expanded={isExpanded}
    >
      {props.children}
    </div>
  );
}

/** @param {import("react").HTMLAttributes<HTMLDivElement>} props  */
function ShowcaseCardDetails(props) {
  const isExpanded = useShowcaseCard().currentExpanded === "details";

  return (
    <div
      className={cn(
        "showcase-card-details",
        props.className,
        isExpanded && "expanded group",
      )}
      data-expanded={isExpanded}
    >
      {props.children}
    </div>
  );
}

/** @param {import("react").ButtonHTMLAttributes<HTMLButtonElement>} props  */
function ShowcaseCardTrigger(props) {
  const setCurrentExpanded = useShowcaseCard().setCurrentExpanded;
  const currentExpanded = useShowcaseCard().currentExpanded;

  return (
    <button
      {...props}
      data-current-expanded={currentExpanded}
      onClick={() =>
        setCurrentExpanded((currentExpanded) => {
          if (currentExpanded === "front") {
            return "details";
          }

          return "front";
        })
      }
      className={cn(
        "showcase-card-trigger absolute bottom-2 right-2",
        "ltr:right-auto rtl:left-2",
        "z-[1]",
        "flex items-center justify-center",
        "size-8 rounded-full bg-white",
        "data-[current-expanded=details]:rotate-180",
        "transition-transform duration-300",
        props.className,
      )}
    >
      {props.children ?? <ChevronUp />}
    </button>
  );
}

export {
  ShowcaseCardProvider,
  ShowcaseCard,
  ShowcaseCardFront,
  ShowcaseCardDetails,
  ShowcaseCardTrigger,
};
